<template>
  <g class="Labels">
    <text
      v-for="(label, i) in labelsProcessed"
      :key="i"
      class="Label"
      :ref="`labels${i}`"
      style="font-weight: bold; user-select: none"
      :style="{
        fontSize: `${fontSize}px`,
        opacity: label.isHidden ? 0 : undefined,
      }"
      :transform="`translate(${label.x}, ${label.y}) rotate(${angle})`"
      :text-anchor="label.textAnchor"
      :dominant-baseline="label.dominantBaseline"
      >{{ label.text }}</text
    >
  </g>
</template>

<script>
// If one or more of these is true, they can't overlap.
// If all are false, they do overlap.
const detectCollisions = (rect1, rect2) =>
  !(
    rect1.right < rect2.left ||
    rect1.left > rect2.right ||
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom
  );

export default {
  name: 'DatalasLabels',

  props: {
    labels: {
      type: Array,
      default: () => [],
    },
    prefix: {
      type: String,
      default: '',
    },
    suffix: {
      type: String,
      default: '',
    },
    format: {
      type: String,
    },
    fontSize: {
      type: Number,
      default: 14,
    },
    detectCollisions: {
      type: Boolean,
      default: true,
    },
    filters: {
      type: Array,
    },
    offsetX: {
      type: Number,
      default: 0,
    },
    offsetY: {
      type: Number,
      default: 0,
    },
    angle: {
      type: Number,
      default: 0,
    },
  },

  data: () => ({
    rects: [],
  }),

  watch: {
    labels() {
      this.rects = [];
      this.$nextTick(this.setRects);
    },
    fontSize() {
      this.rects = [];
      this.$nextTick(this.setRects);
    },
  },

  computed: {
    formatter() {
      return this.format ? this.$format(this.format) : (x) => x;
    },

    labelsPreprocessed() {
      return this.labels
        .filter(
          ({ datum }, i, arr) =>
            !this.filters ||
            this.filters.every((filter) => filter(datum, i, arr)),
        )
        .map((label) => ({
          ...label,
          x: label.x + this.offsetX,
          y: label.y + this.offsetY,
          text: `${this.prefix}${this.formatter(label.value)}${this.suffix}`,
        }));
    },

    labelsProcessed() {
      if (!this.detectCollisions) return this.labelsPreprocessed;

      return this.labelsPreprocessed
        .map((label, i) => ({ ...label, rect: this.rects[i] }))
        .reduce(
          (acc, item, i, arr) => [
            ...acc,
            {
              ...item,
              isHidden:
                item.rect &&
                [...acc, ...arr.slice(i + 1)].some(
                  (other) =>
                    !other.isHidden &&
                    item.value <= other.value &&
                    detectCollisions(item.rect, other.rect),
                ),
            },
          ],
          [],
        );
    },
  },

  methods: {
    setRects() {
      // Labels may be gone by the time setTimeout fires
      const thereAreLabelRefs = Object.keys(this.$refs).some(
        (refId, i) => refId === `labels${i}` && this.$refs[refId].getBBox,
      );

      if (!thereAreLabelRefs) return;

      this.rects = this.labelsPreprocessed.map(({ x, y }, i) => {
        const { width, height } = this.$refs[`labels${i}`].getBBox();
        return {
          top: y,
          right: x + width,
          bottom: y + height,
          left: x,
        };
      });
    },
  },

  mounted() {
    // Not sure why setTimeout works and nextTick doesn't, may be related to css
    // transitions
    setTimeout(this.setRects, 0);
  },
};
</script>
